import {
  CustomRevenueRecognitionTypes,
  FixedFeeRevenueRecognitionTypes,
  SubscriptionRevenueRecognitionTypes,
  TimeMaterialRevenueRecognitionTypes,
} from 'api/settings-financials/types';
import type { FieldOption } from 'api/tasks';
import { BaseCalendarConfig } from 'components/organisms/projects-report/common/range-picker/calendar-engine';

interface AdvancedSettingFfRevRec {
  eventType: SettingType.RevenueRecognitionAtProjectLevel;
  eventValue: boolean;
  projectWithStatusesNotification?: FieldOption<number>['value'][] | null;
  allowedRevenueRecognitionTypes: Record<
    | FixedFeeRevenueRecognitionTypes
    | SubscriptionRevenueRecognitionTypes
    | CustomRevenueRecognitionTypes,
    boolean
  >;
  allowedCustomRevenueRecognitionConfigs: number[];
}

interface AdvancedSettingTmRevRec {
  eventType: SettingType.TMRevenueRecognitionAtProjectLevel;
  eventValue: boolean;
  projectWithStatusesNotification?: FieldOption<number>['value'][] | null;
  allowedRevenueRecognitionTypes: Record<
    | TimeMaterialRevenueRecognitionTypes
    | SubscriptionRevenueRecognitionTypes
    | CustomRevenueRecognitionTypes,
    boolean
  >;
  allowedCustomRevenueRecognitionConfigs: number[];
}

export type AdvancedSettingRevRec =
  | AdvancedSettingFfRevRec
  | AdvancedSettingTmRevRec;

export interface AdvancedSettingCalendarConfig {
  eventType: SettingType.CustomCalendarConfiguration;
  eventValue: boolean;
  calendarConfig: BaseCalendarConfig;
}

export type AdvancedSetting =
  | {
      eventType: Exclude<
        SettingType,
        | SettingType.StopScheduledNotifications
        | SettingType.RevenueRecognitionAtProjectLevel
        | SettingType.CustomCalendarConfiguration
      >;
      eventValue: boolean;
      projectWithStatusesNotification?: FieldOption<number>['value'][] | null;
      allowedRevenueRecognitionTypes?: Record<
        FixedFeeRevenueRecognitionTypes | SubscriptionRevenueRecognitionTypes,
        boolean
      >;
    }
  | {
      eventType: SettingType.StopScheduledNotifications;
      eventValue: boolean;
      projectWithStatusesNotification: FieldOption<number>['value'][] | null;
      allowedRevenueRecognitionTypes?: Record<
        FixedFeeRevenueRecognitionTypes | SubscriptionRevenueRecognitionTypes,
        boolean
      >;
    }
  | AdvancedSettingRevRec
  | AdvancedSettingCalendarConfig;

export enum SettingsGroup {
  TaskStatusAutomation = 'TaskStatusAutomation',
  PhaseAutomation = 'PhaseAutomation',
  ScheduledNotifications = 'ScheduledNotifications',
  NewDependenciesAndLag = 'NewDependenciesAndLag',
}

export enum SettingType {
  MarkTaskAsCompletedIfSubTaskIs = 'MARK_PARENT_TASK_TO_COMPLETED_IF_SUBTASK_IS_COMPLETED',
  MarkTaskAsInProgressIfSubTaskIs = 'MARK_PARENT_TASK_TO_IN_PROGRESS_IF_SUBTASK_CHANGES',
  MarkSubTasksAsCompletedIfTaskIs = 'MARK_SUBTASKS_COMPLETED_IF_PARENT_IS_COMPLETED',
  AlignPhaseDates = 'BOUND_PHASE_DATES_TO_TASKS_START_AND_DUE_DATE_BOUNDARIES',
  StopScheduledNotifications = 'STOP_SCHEDULE_NOTIFICATION_PROJECT_STATUSES',
  OtpAuthentication = 'CUSTOMER_AUTHENTICATION_OTP_ENABLED',
  RevenueRecognitionAtProjectLevel = 'ALLOW_USERS_TO_SET_REV_REC_CONFIG_AT_PROJECT_LEVEL',
  TMRevenueRecognitionAtProjectLevel = 'ALLOW_USERS_TO_SET_TM_REV_REC_CONFIG_AT_PROJECT_LEVEL',
  EnableNewDependenciesAndLag = 'ENABLE_NEW_DEPENDENCIES_AND_LAG',
  CustomCalendarConfiguration = 'CUSTOM_CALENDAR_CONFIGURATION',
}

export const SettingsByGroup = {
  [SettingsGroup.TaskStatusAutomation]: [
    SettingType.MarkSubTasksAsCompletedIfTaskIs,
    SettingType.MarkTaskAsCompletedIfSubTaskIs,
    SettingType.MarkTaskAsInProgressIfSubTaskIs,
  ],
  [SettingsGroup.PhaseAutomation]: [SettingType.AlignPhaseDates],
  [SettingsGroup.NewDependenciesAndLag]: [
    SettingType.EnableNewDependenciesAndLag,
  ],
  [SettingsGroup.ScheduledNotifications]: [
    SettingType.StopScheduledNotifications,
  ],
};
